/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Compute Advisor (redesign artboard 24). Ranks QuickPods / TrainPods GPUs for
// a described workload using the advisor-service (same backend as the
// <podstack-advisor> widget). Home's "Recommend" box deep-links here with ?q=.

import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Page, Card, Chip, Tag, Tone, SectionHeader, Skeleton } from '../components/console/ui';
import { CompassIc } from '../components/console/icons';
import {
  recommendCompute,
  AdvisorRecommendation,
  AdvisorProduct,
} from '../components/home/advisorClient';

const PRESETS = ['Fine-tune Llama 3 8B', 'Serve a chatbot', 'Stable Diffusion XL', 'Train from scratch'];

const PRODUCT: Record<AdvisorProduct, { label: string; tone: Tone }> = {
  quickpods: { label: 'QuickPods', tone: 'warn' },
  trainpods: { label: 'TrainPods', tone: 'info' },
  inference: { label: 'Inference', tone: 'accent' },
  'dc-suite': { label: 'DC Suite', tone: 'off' },
};

const inr = (n: number) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/** QuickPods CTAs that point at this portal become in-app links; everything else opens in a new tab. */
const ctaTarget = (r: AdvisorRecommendation): { internal?: string; external?: string } => {
  if (r.product === 'quickpods') {
    try {
      const u = new URL(r.cta_url, window.location.origin);
      const base = '/portal';
      if (u.origin === window.location.origin || u.pathname.startsWith(`${base}/`)) {
        const path = u.pathname.startsWith(`${base}/`) ? u.pathname.slice(base.length) : u.pathname;
        return { internal: `${path}${u.search}` || '/pods/create' };
      }
    } catch {
      // fall through to the default launch flow
    }
    return r.cta_url ? { external: r.cta_url } : { internal: '/pods/create' };
  }
  return { external: r.cta_url };
};

const ctaLabel = (p: AdvisorProduct) =>
  p === 'quickpods' ? 'Launch pod' : p === 'dc-suite' ? 'Talk to us' : 'View offer';

const RecommendationCard: React.FC<{ r: AdvisorRecommendation }> = ({ r }) => {
  const product = PRODUCT[r.product] ?? { label: r.product, tone: 'off' as Tone };
  const target = ctaTarget(r);
  const primary = r.rank === 1;
  const btnCls = `${primary ? 'ps-btn' : 'ps-btn2'} max-[820px]:w-full max-[820px]:min-h-[44px]`;
  return (
    <article className="ps-card p-5 flex flex-wrap gap-4 items-start">
      <span
        className="w-8 h-8 rounded-full grid place-items-center bg-ps-accent-soft text-ps-accent font-bold flex-none"
        aria-label={`Rank ${r.rank}`}
      >
        {r.rank}
      </span>
      <div className="flex-[1_1_320px] min-w-0 flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="m-0 text-[17px] font-semibold">
            {r.gpu}
            {r.vram_gb > 0 && ` ${r.vram_gb} GB`}
          </h3>
          <Tag tone={product.tone}>{product.label}</Tag>
          {r.region && <Tag tone="off">{r.region}</Tag>}
        </div>
        {r.why && <p className="m-0 text-sm text-ps-muted leading-relaxed">{r.why}</p>}
        <div className="flex flex-wrap gap-x-7 gap-y-3">
          {r.price_inr_hr > 0 && (
            <div className="flex flex-col gap-0.5 text-[13px] text-ps-subtle">
              <b className="text-ps-text text-[15px] font-semibold">{inr(r.price_inr_hr)}/hr</b>
              {r.product === 'quickpods' ? 'Price' : 'From'}
            </div>
          )}
          {r.est_cost_hint && (
            <div className="flex flex-col gap-0.5 text-[13px] text-ps-subtle min-w-0">
              <b className="text-ps-text text-[15px] font-semibold break-words">{r.est_cost_hint}</b>
              Estimate
            </div>
          )}
          {r.gpu_fraction && (
            <div className="flex flex-col gap-0.5 text-[13px] text-ps-subtle">
              <b className="text-ps-text text-[15px] font-semibold">
                {r.gpu_fraction} GPU · {r.vcpus} vCPU · {r.ram_gb} GB
              </b>
              {r.storage ? `Size · storage: ${r.storage}` : 'Size'}
            </div>
          )}
        </div>
      </div>
      <div className="flex-none max-[820px]:w-full">
        {target.internal ? (
          <Link to={target.internal} className={btnCls}>
            {ctaLabel(r.product)}
          </Link>
        ) : target.external ? (
          <a href={target.external} target="_blank" rel="noopener noreferrer" className={btnCls}>
            {ctaLabel(r.product)}
          </a>
        ) : null}
      </div>
    </article>
  );
};

const Advisor: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workload, setWorkload] = useState(() => searchParams.get('q') || '');
  const autoRan = useRef(false);

  const advisor = useMutation({
    mutationFn: (text: string) => recommendCompute(text),
  });
  const { mutate } = advisor;

  const submit = (text = workload) => {
    const t = text.trim();
    if (!t || advisor.isPending) return;
    advisor.mutate(t);
  };

  // Deep link from Home: /advisor?q=… runs once, then the param is dropped.
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && !autoRan.current) {
      autoRan.current = true;
      mutate(q.trim().slice(0, 500));
      const next = new URLSearchParams(searchParams);
      next.delete('q');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, mutate]);

  const result = advisor.data;
  const recs = [...(result?.recommendations || [])].sort((a, b) => a.rank - b.rank);

  return (
    <Page className="!max-w-[1100px]">
      <div className="flex flex-col gap-1.5">
        <div className="ps-eyebrow">Compute Advisor</div>
        <h1 className="m-0 text-2xl sm:text-[28px] font-medium text-ps-text">Which GPU fits your job?</h1>
        <p className="m-0 text-ps-muted text-[15px]">
          Ranks QuickPods and TrainPods GPUs using live pricing and availability.
        </p>
      </div>

      <Card as="section" aria-label="Describe your workload" className="p-5 flex flex-col gap-3.5">
        <form
          className="flex flex-col gap-3.5"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="sr-only" htmlFor="advisor-workload">
            Describe your workload
          </label>
          <textarea
            id="advisor-workload"
            rows={3}
            maxLength={500}
            value={workload}
            onChange={(e) => setWorkload(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="e.g. Fine-tune a 13B model on our internal docs, keep the bill under ₹5,000"
            className="ps-input py-2.5 resize-y min-h-[88px] leading-relaxed"
          />
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((p) => (
              <Chip key={p} on={workload === p} onClick={() => setWorkload(p)}>
                {p}
              </Chip>
            ))}
            <button
              type="submit"
              className="ps-btn ml-auto min-h-[44px] max-[820px]:w-full max-[820px]:ml-0"
              disabled={!workload.trim() || advisor.isPending}
            >
              {advisor.isPending ? 'Consulting the catalog…' : 'Recommend'}
            </button>
          </div>
        </form>
      </Card>

      <section className="flex flex-col gap-3.5" aria-labelledby="advisor-results" aria-live="polite">
        <SectionHeader
          id="advisor-results"
          title="Recommendations"
          action={
            result?.source === 'heuristic' ? (
              <span className="text-[13px] text-ps-subtle">Quick catalog match - figures exact, reasoning simplified</span>
            ) : result ? (
              <span className="text-[13px] text-ps-subtle">Live prices from the Podstack catalog</span>
            ) : undefined
          }
        />

        {advisor.isPending && (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[120px] w-full rounded-[14px]" />
            ))}
          </div>
        )}

        {advisor.isError && !advisor.isPending && (
          <Card className="p-5 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-ps-err">
              We couldn’t reach the advisor right now - please try again in a minute.
            </span>
            <button type="button" className="ps-btn2" onClick={() => submit()} disabled={!workload.trim()}>
              Try again
            </button>
          </Card>
        )}

        {!advisor.isPending && result && (
          <>
            {result.summary && <p className="m-0 text-[15px] leading-relaxed">{result.summary}</p>}
            {recs.length === 0 ? (
              <Card className="p-5 text-sm text-ps-muted">
                No GPU in the catalog matches that workload. Try loosening the budget or describing the model size.
              </Card>
            ) : (
              recs.map((r) => <RecommendationCard key={`${r.rank}-${r.gpu}-${r.region}`} r={r} />)
            )}
            {result.assumptions?.length > 0 && (
              <div className="text-[13px] text-ps-subtle">
                <div className="mb-1">Assumptions</div>
                <ul className="m-0 pl-5 list-disc flex flex-col gap-0.5">
                  {result.assumptions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {!advisor.isPending && !result && !advisor.isError && (
          <Card className="p-5 flex items-center gap-3.5 text-sm text-ps-muted">
            <CompassIc className="w-5 h-5 text-ps-accent flex-none" />
            Describe the model, data size and budget - or pick an example above - and the advisor will rank the GPUs that
            fit, with price per hour and an estimate.
          </Card>
        )}

        <p className="m-0 text-xs text-ps-subtle">Protected by reCAPTCHA. Recommendations are estimates; check the launch page for final pricing.</p>
      </section>
    </Page>
  );
};

export default Advisor;
